// components/LSSF/StudentsList.jsx
import React, { useState, useEffect } from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import { MdDelete, MdSearch, MdVisibility, MdEdit } from 'react-icons/md';

const StudentsList = () => {
  const base_url = import.meta.env.VITE_LOCAL_BASE_URL;
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const limit = 20;

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    father_name: '',
    mother_name: '',
    mobile_num: '',
    dob: '',
    mail_id: '',
    password: '',
    city: '',
    state: '',
    exam_attempted: ''
  });

  const resetForm = () => {
    setFormData({
      firstname: '',
      lastname: '',
      father_name: '',
      mother_name: '',
      mobile_num: '',
      dob: '',
      mail_id: '',
      password: '',
      city: '',
      state: '',
      exam_attempted: ''
    });
    setIsEditing(false);
    setSelectedStudent(null);
  };

  const getStudents = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${base_url}/api/v1/users`);
      setData(res.data.data);
      setFilteredData(res.data.data);
      const total = Math.ceil(res.data.data.length / limit);
      setTotalPages(total > 0 ? total : 1);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching students:', err);
      toast.error('Failed to fetch students');
      setLoading(false);
    }
  };

  useEffect(() => {
    getStudents();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredData(data);
    } else {
      const searchLower = searchTerm.toLowerCase();
      const filtered = data.filter(item => {
        return (
          (item.firstname && item.firstname.toLowerCase().includes(searchLower)) ||
          (item.lastname && item.lastname.toLowerCase().includes(searchLower)) ||
          (item.mail_id && item.mail_id.toLowerCase().includes(searchLower)) ||
          (item.mobile_num && item.mobile_num.includes(searchLower)) ||
          (item.city && item.city.toLowerCase().includes(searchLower)) ||
          (item.state && item.state.toLowerCase().includes(searchLower))
        );
      });
      setFilteredData(filtered);
    }
  }, [searchTerm, data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isEditing && selectedStudent) {
        const { password, ...updateData } = formData;
        await axios.put(`${base_url}/api/v1/users/${selectedStudent.id}`, updateData);
        toast.success('Student updated successfully');
      } else {
        await axios.post(`${base_url}/api/v1/users/register`, formData);
        toast.success('Student added successfully');
      }
      resetForm();
      setShowForm(false);
      getStudents();
    } catch (err) {
      console.error('Submit error:', err);
      toast.error(err.response?.data?.message || 'Failed to save student');
    }
  };

  const deleteStudent = async (id) => {
    const choice = window.confirm('Are you sure you want to delete this student?');
    if (choice) {
      try {
        await axios.delete(`${base_url}/api/v1/users/${id}`);
        toast.success('Student deleted successfully');
        getStudents();
      } catch (err) {
        console.error('Delete error:', err);
        toast.error('Failed to delete student');
      }
    }
  };

  const viewDetails = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
  };

  const editStudent = (student) => {
    setFormData({
      firstname: student.firstname || '',
      lastname: student.lastname || '',
      father_name: student.father_name || '',
      mother_name: student.mother_name || '',
      mobile_num: student.mobile_num || '',
      dob: student.dob ? student.dob.split('T')[0] : '',
      mail_id: student.mail_id || '',
      password: '',
      city: student.city || '',
      state: student.state || '',
      exam_attempted: student.exam_attempted || ''
    });
    setSelectedStudent(student);
    setIsEditing(true);
    setShowForm(true);
  };

  const paginatedData = filteredData.slice((page - 1) * limit, page * limit);

  return (
    <div className="page-wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="card">
              <div className="card-header row d-flex justify-content-between align-items-center">
                <div className="col-md-4">
                  <h4 className="mb-0">Students List</h4>
                </div>
                <div className="col-md-4">
                  <div className="input-group">
                    <span className="input-group-text">
                      <MdSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name, email, mobile..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="col-md-4 text-end">
                  <button 
                    className="btn btn-primary btn-sm"
                    onClick={() => {
                      resetForm();
                      setShowForm(!showForm);
                    }}
                  >
                    {showForm ? 'Close Form' : 'Add New Student'}
                  </button>
                </div>
              </div>
              <div className="card-body">
                {/* Add/Edit Form */}
                {showForm && (
                  <div className="card mb-4">
                    <div className="card-header bg-primary text-white">
                      <h5 className="mb-0">{isEditing ? 'Edit Student' : 'Add New Student'}</h5>
                    </div>
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label className="form-label">First Name *</label>
                            <input type="text" className="form-control" name="firstname" value={formData.firstname} onChange={handleChange} required />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Last Name *</label>
                            <input type="text" className="form-control" name="lastname" value={formData.lastname} onChange={handleChange} required />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Father Name</label>
                            <input type="text" className="form-control" name="father_name" value={formData.father_name} onChange={handleChange} />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Mother Name</label>
                            <input type="text" className="form-control" name="mother_name" value={formData.mother_name} onChange={handleChange} />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Mobile *</label>
                            <input type="text" className="form-control" name="mobile_num" value={formData.mobile_num} onChange={handleChange} required />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Email *</label>
                            <input type="email" className="form-control" name="mail_id" value={formData.mail_id} onChange={handleChange} required />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Date of Birth</label>
                            <input type="date" className="form-control" name="dob" value={formData.dob} onChange={handleChange} />
                          </div>
                          {!isEditing && (
                            <div className="col-md-6 mb-3">
                              <label className="form-label">Password *</label>
                              <input type="password" className="form-control" name="password" value={formData.password} onChange={handleChange} required={!isEditing} />
                            </div>
                          )}
                          <div className="col-md-6 mb-3">
                            <label className="form-label">City</label>
                            <input type="text" className="form-control" name="city" value={formData.city} onChange={handleChange} />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">State</label>
                            <input type="text" className="form-control" name="state" value={formData.state} onChange={handleChange} />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Exam Attempted</label>
                            <input type="text" className="form-control" name="exam_attempted" value={formData.exam_attempted} onChange={handleChange} placeholder="NEET, AIIMS etc." />
                          </div>
                        </div>
                        <div className="text-end">
                          <button type="button" className="btn btn-secondary me-2" onClick={() => {
                            resetForm();
                            setShowForm(false);
                          }}>Cancel</button>
                          <button type="submit" className="btn btn-primary">{isEditing ? 'Update' : 'Submit'}</button>
                        </div>
                      </form>
                    </div>
                  </div>
                )}

                {/* Data Table */}
                <div className="table-responsive">
                  <table className="table table-bordered shadow-sm mt-3" cellPadding={5}>
                    <thead className="bg-light">
                      <tr>
                        <th>S.No.</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>City</th>
                        <th>State</th>
                        <th>Exam</th>
                        <th>Date</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          {[...Array(9)].map((_, i) => (
                            <td key={i}><Skeleton count={1} /></td>
                          ))}
                        </tr>
                      ) : paginatedData.length > 0 ? (
                        paginatedData.map((item, index) => (
                          <tr key={item.id}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{item.firstname} {item.lastname}</td>
                            <td>{item.mail_id}</td>
                            <td>{item.mobile_num}</td>
                            <td>{item.city || '-'}</td>
                            <td>{item.state || '-'}</td>
                            <td>{item.exam_attempted || '-'}</td>
                            <td>{new Date(item.created_at).toLocaleDateString()}</td>
                            <td>
                              <button className="btn btn-sm btn-info me-1" onClick={() => viewDetails(item)}>
                                <MdVisibility size={18} />
                              </button>
                              <button className="btn btn-sm btn-warning me-1" onClick={() => editStudent(item)}>
                                <MdEdit size={18} />
                              </button>
                              <button className="btn btn-sm btn-danger" onClick={() => deleteStudent(item.id)}>
                                <MdDelete size={18} />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr className="text-center text-danger">
                          <td colSpan={9}>No students found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                {!loading && filteredData.length > 0 && (
                  <div className="row mt-4">
                    <div className="col-md-12">
                      <div className="alert alert-info">
                        <strong>Summary:</strong> Total Students: {filteredData.length}
                      </div>
                    </div>
                  </div>
                )}

                {/* Pagination */}
                {filteredData.length > limit && (
                  <div className="d-flex justify-content-center align-items-center gap-3 mt-4">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
                    <span>Page {page} of {totalPages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {showModal && selectedStudent && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">Student Details</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <strong>Full Name:</strong>
                    <p>{selectedStudent.firstname} {selectedStudent.lastname}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Email:</strong>
                    <p>{selectedStudent.mail_id}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Mobile:</strong>
                    <p>{selectedStudent.mobile_num}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Date of Birth:</strong>
                    <p>{selectedStudent.dob ? new Date(selectedStudent.dob).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Father Name:</strong>
                    <p>{selectedStudent.father_name || 'N/A'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Mother Name:</strong>
                    <p>{selectedStudent.mother_name || 'N/A'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>City:</strong>
                    <p>{selectedStudent.city || 'N/A'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>State:</strong>
                    <p>{selectedStudent.state || 'N/A'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Exam Attempted:</strong>
                    <p>{selectedStudent.exam_attempted || 'N/A'}</p>
                  </div>
                  <div className="col-md-6 mb-3">
                    <strong>Registration Date:</strong>
                    <p>{new Date(selectedStudent.created_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default StudentsList;